import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { ProductCard } from '../components/ProductCard';
import { SearchFilters } from '../components/SearchFilters';
import { Button } from '../components/ui/button';
import { productsAPI } from '../utils/api';
import { toast } from 'sonner';

export const CataloguePage = () => {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;

  // Reload products when filters or search change (reset to page 0)
  useEffect(() => {
    setPage(0);
    setProducts([]);
    setHasMore(true);
    loadProducts(0, true);
  }, [filters, searchQuery]);

  // Reload when navigating to this page
  useEffect(() => {
    if (location.pathname === '/catalogue') {
      setPage(0);
      setProducts([]);
      setHasMore(true);
      loadProducts(0, true);
    }
  }, [location.pathname]);

  const loadProducts = async (pageNum = 0, isNewSearch = false) => {
    if (isNewSearch) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    setError(null);
    
    try {
      const params = {
        search: searchQuery || undefined,
        category: filters.category && filters.category !== 'all' ? filters.category : undefined,
        stock_status: filters.stock_status && filters.stock_status !== 'all' ? filters.stock_status : undefined,
        sort_by: filters.sort_by || 'created_at',
        sort_order: 'desc',
        limit: PAGE_SIZE,
        skip: pageNum * PAGE_SIZE
      };

      if (filters.priceRange && filters.priceRange !== 'all') {
        try {
          const range = JSON.parse(filters.priceRange);
          if (range.min !== undefined) params.min_price = range.min;
          if (range.max !== null) params.max_price = range.max;
        } catch (e) {
          console.error('Failed to parse price range:', e);
        }
      }

      console.log(`Loading products: page ${pageNum}, skip ${params.skip}, limit ${params.limit}`);
      const response = await productsAPI.getAll(params);
      console.log(`Loaded ${response.data.length} products`);
      
      if (isNewSearch) {
        setProducts(response.data);
      } else {
        setProducts(prev => [...prev, ...response.data]);
      }
      
      // If we got less than PAGE_SIZE, there are no more products
      setHasMore(response.data.length === PAGE_SIZE);
      setError(null);
    } catch (error) {
      console.error('Failed to load products:', error);
      setError('Failed to load products. Please try again.');
      toast.error('Failed to load products');
      if (isNewSearch) {
        setProducts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadProducts(nextPage, false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <SearchFilters onSearch={setSearchQuery} onFilterChange={setFilters} filters={filters} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-consortium-green"></div>
          </div>
        ) : products.length > 0 ? (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-slate-600">{products.length} products loaded</p>
              {hasMore && (
                <p className="text-sm text-slate-500">Scroll down or click "Load More" to see more products</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            
            {hasMore && (
              <div className="mt-8 text-center">
                <Button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="bg-consortium-green hover:bg-consortium-green-hover px-8 py-3"
                  data-testid="load-more-btn"
                >
                  {loadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading more products...
                    </>
                  ) : (
                    `Load More Products (${products.length} of many)`
                  )}
                </Button>
              </div>
            )}
            
            {!hasMore && products.length > 0 && (
              <div className="mt-8 text-center">
                <p className="text-slate-500">✓ All {products.length} products loaded</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="font-barlow text-xl font-semibold text-slate-900 mb-2">No products found</h3>
            <p className="text-slate-600">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};
